import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Snackbar,
  Chip,
  IconButton,
} from '@mui/material';
import { Edit, Store as StoreIcon } from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import storeService from '../../services/storeService';
import userService from '../../services/userService';
import { Store, User } from '../../types';
import { USER_ROLE_OPTIONS } from '../../constants';
import ProfileAvatar from '../../components/common/ProfileAvatar';

const StoreManagerManagementPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [stores, setStores] = useState<Store[]>([]);
  const [storeManagers, setStoreManagers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [selectedManagerId, setSelectedManagerId] = useState<string>('');
  const [demoteToRole, setDemoteToRole] = useState<string>('associate');
  const [updating, setUpdating] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [storesResponse, managersResponse] = await Promise.all([
        storeService.getStores({ limit: 1000 }),
        userService.getUsers({ role: 'store_manager', limit: 1000 }),
      ]);

      setStores(storesResponse.stores || []);
      setStoreManagers(managersResponse.users || []);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to fetch data',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (store: Store) => {
    setSelectedStore(store);
    setSelectedManagerId(store.manager?.id || '');
    setDemoteToRole('associate'); // Default to associate
    setDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!selectedStore) return;

    try {
      setUpdating(true);

      const oldManagerId = selectedStore.manager?.id;
      // Only need demoteToRole when there IS a current manager AND a new one is being assigned
      const isReplacing = !!oldManagerId && !!selectedManagerId && oldManagerId !== selectedManagerId;

      if (isReplacing && !demoteToRole) {
        setSnackbar({
          open: true,
          message: 'Please select a role for the current manager being replaced',
          severity: 'warning',
        });
        setUpdating(false);
        return;
      }

      await storeService.updateStoreManager(
        selectedStore.id,
        selectedManagerId || null,
        isReplacing ? demoteToRole : undefined
      );

      setSnackbar({
        open: true,
        message: 'Store manager updated successfully',
        severity: 'success',
      });

      setDialogOpen(false);
      fetchData();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to update store manager',
        severity: 'error',
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedStore(null);
    setSelectedManagerId('');
    setDemoteToRole('associate');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <StoreIcon sx={{ fontSize: 32, color: '#081122' }} />
        <Typography variant="h4" sx={{ color: '#081122', fontWeight: 600 }}>
          Store Manager Management
        </Typography>
      </Box>

      <Paper sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Store</TableCell>
                <TableCell>Cluster</TableCell>
                <TableCell>Store Manager</TableCell>
                <TableCell>Manager Employee ID</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stores.map((store) => (
                <TableRow key={store.id}>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {store.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        {store.code}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {store.cluster?.name || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {store.manager ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ProfileAvatar
                          imageUrl={store.manager.profile_image_url}
                          firstName={store.manager.first_name}
                          lastName={store.manager.last_name}
                          sx={{ width: 32, height: 32 }}
                        />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {store.manager.first_name} {store.manager.last_name}
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ color: '#999' }}>
                        No Manager Assigned
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {store.manager?.employee_id || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleEditClick(store)}
                      sx={{ color: '#2196f3' }}
                    >
                      <Edit />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Update Store Manager</DialogTitle>
        <DialogContent>
          {selectedStore && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Store: <strong>{selectedStore.name}</strong> ({selectedStore.code})
              </Alert>

              {/* Current manager info */}
              {selectedStore.manager && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Current Manager: <strong>
                    {selectedStore.manager.first_name} {selectedStore.manager.last_name}
                  </strong> ({selectedStore.manager.employee_id})
                </Alert>
              )}

              {/* Unassign warning */}
              {selectedStore.manager && selectedManagerId === '' && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <strong>{selectedStore.manager.first_name} {selectedStore.manager.last_name}</strong> will be removed as manager of this store. They will keep their Store Manager role and can be reassigned later.
                </Alert>
              )}

              {/* Transfer warning */}
              {(() => {
                const transferFrom = stores.find(
                  (s) => s.manager?.id === selectedManagerId && s.id !== selectedStore.id
                );
                return transferFrom ? (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    This manager currently manages <strong>{transferFrom.name} ({transferFrom.code})</strong>. They will be transferred to this store and <strong>{transferFrom.name}</strong> will have no manager assigned.
                  </Alert>
                ) : null;
              })()}

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Store Manager</InputLabel>
                <Select
                  value={selectedManagerId}
                  label="Store Manager"
                  onChange={(e) => setSelectedManagerId(e.target.value)}
                >
                  <MenuItem value="">No Manager (Unassign)</MenuItem>
                  {storeManagers
                    .filter((manager) => manager.id !== selectedStore.manager?.id)
                    .map((manager) => {
                      const managingStore = stores.find(
                        (s) => s.manager?.id === manager.id && s.id !== selectedStore.id
                      );
                      return (
                        <MenuItem key={manager.id} value={manager.id}>
                          {manager.first_name} {manager.last_name} ({manager.employee_id})
                          {managingStore && (
                            <Typography component="span" variant="caption" sx={{ ml: 1, color: '#ed6c02' }}>
                              — Transfer from {managingStore.code}
                            </Typography>
                          )}
                        </MenuItem>
                      );
                    })}
                </Select>
              </FormControl>

              {/* Demotion selector: only when replacing current manager with someone new */}
              {selectedStore.manager &&
               selectedManagerId &&
               selectedManagerId !== selectedStore.manager.id && (
                <FormControl fullWidth>
                  <InputLabel>Demote Current Manager To</InputLabel>
                  <Select
                    value={demoteToRole}
                    label="Demote Current Manager To"
                    onChange={(e) => setDemoteToRole(e.target.value)}
                  >
                    {USER_ROLE_OPTIONS.filter(
                      (role) =>
                        role.value !== 'super_admin' &&
                        role.value !== 'cluster_level_manager' &&
                        role.value !== 'store_manager'
                    ).map((role) => (
                      <MenuItem key={role.value} value={role.value}>
                        {role.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography variant="caption" sx={{ mt: 1, color: '#666' }}>
                    The current manager will be changed to this role.
                  </Typography>
                </FormControl>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={updating}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdate}
            variant="contained"
            disabled={updating}
            startIcon={updating ? <CircularProgress size={16} /> : null}
          >
            {updating ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
};

export default StoreManagerManagementPage;

